import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.patch('/auth/me', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  deleteAccount: () => api.delete('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Companies
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  getOne: (id) => api.get(`/companies/${id}`),
  getStatus: (id) => api.get(`/companies/${id}/status`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.patch(`/companies/${id}`, data),
};

// Objectives
export const objectivesAPI = {
  getAll: (companyId) => api.get('/objectives', { params: companyId ? { company_id: companyId } : {} }),
  getOne: (id) => api.get(`/objectives/${id}`),
  create: (data) => api.post('/objectives', data),
};

// Key Results
export const keyResultsAPI = {
  getAll: (params) => api.get('/key-results', { params }),
  getOne: (id) => api.get(`/key-results/${id}`),
  create: (data) => api.post('/key-results', data),
  update: (id, data) => api.patch(`/key-results/${id}`, data),
};

// Tasks
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.patch(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  updateProgress: (id, progress) => api.patch(`/tasks/${id}/progress`, null, { params: { progress } }),
  assign: (id, assigneeId) => api.patch(`/tasks/${id}/assign`, null, { params: { assignee_id: assigneeId } }),
  unassign: (id) => api.delete(`/tasks/${id}/assign`),
  getComments: (id) => api.get(`/tasks/${id}/comments`),
  addComment: (id, content) => api.post(`/tasks/${id}/comments`, { content }),
  generateChecklist: (id) => api.post(`/tasks/${id}/generate-checklist`),
  toggleChecklistItem: (taskId, itemId, completed) => api.patch(`/tasks/${taskId}/checklist/${itemId}`, null, { params: { completed } }),
  assignChecklistItem: (taskId, itemId, assigneeId) => api.patch(`/tasks/${taskId}/checklist/${itemId}/assign`, null, { params: { assignee_id: assigneeId } }),
  unassignChecklistItem: (taskId, itemId) => api.delete(`/tasks/${taskId}/checklist/${itemId}/assign`),
};

// Notes
export const notesAPI = {
  getAll: (params) => api.get('/notes', { params }),
  create: (data) => api.post('/notes', data),
};

// Check-ins
export const checkinsAPI = {
  getAll: (checkType) => api.get('/checkins', { params: { check_type: checkType } }),
  create: (data) => api.post('/checkins', data),
};

// Delegations
export const delegationsAPI = {
  getAll: () => api.get('/delegations'),
  create: (data) => api.post('/delegations', data),
};

// AI Drafts
export const aiDraftsAPI = {
  getAll: (status) => api.get('/ai-drafts', { params: { status } }),
  update: (id, data) => api.patch(`/ai-drafts/${id}`, data),
};

// AI Generation
export const aiAPI = {
  generateTasks: (krId) => api.post(`/ai/generate-tasks/${krId}`),
  dailyTop3: () => api.post('/ai/daily-top-3'),
  analyzeBlockers: () => api.post('/ai/analyze-blockers'),
  suggestDelegations: () => api.post('/ai/suggest-delegations'),
  weeklyPlan: (companyId) => api.post(`/ai/weekly-plan/${companyId}`),
  generateObjectives: (companyId, focusArea, count = 3) => 
    api.post(`/ai/generate-objectives/${companyId}`, null, { params: { focus_area: focusArea, count } }),
  generateKeyResults: (objectiveId, count = 3) => 
    api.post(`/ai/generate-key-results/${objectiveId}`, null, { params: { count } }),
};

// Users (Admin)
export const usersAPI = {
  getAll: () => api.get('/users'),
  updateRole: (id, role) => api.patch(`/users/${id}/role`, null, { params: { role } }),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Alerts
export const alertsAPI = {
  getAll: () => api.get('/alerts'),
  markRead: (id) => api.patch(`/alerts/${id}/read`),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
